import { useState } from 'react'
import { reportsApi } from '~/api/reports'
import { useToast } from '~/design_system'
import { useSelectItemsContext } from '~/hooks/useSelectItems'
import { useWaitAndDownload } from '~/reports/table/helpers'

export const maxSelectableReports = 4

/*
Parent must be surrounded with <SelectItemsProvider /> from '~/hooks/useSelectItems'
 */
export const useBulkExport = canVsyncReport => {
  const [isBulkExporting, setIsBulkExporting] = useState(false)
  const { selectedItems, isItemSelected, toggleItem, deselectAllItems } = useSelectItemsContext()
  const maxSelectableReached = selectedItems.length >= maxSelectableReports
  const toast = useToast()

  const isReportSelectable = report => {
    // 0. Disabled for standard reports
    if (report.report_type === 'standard') return false

    // 1. Disabled if exporting is in progress.
    if (isBulkExporting) return false
    // Allow more than maxSelectableReports if user has canVsyncReport permission
    if (!isItemSelected(report) && canVsyncReport) return true
    // 2. Disabled if unchecked and max selectable reached.
    if (!isItemSelected(report) && maxSelectableReached) return false

    return true
  }

  const onSelectReport = (report, event) => {
    if (isReportSelectable(report)) toggleItem(report)
    else if (maxSelectableReached)
      toast({
        description: `Cannot select more than ${maxSelectableReports} reports`,
        status: 'info',
        duration: 2000,
        isClosable: true
      })
    event.stopPropagation()
    event.preventDefault()
  }

  const waitAndDownload = useWaitAndDownload()
  const onBulkExport = async () => {
    setIsBulkExporting(true)
    const onFinish = () => {
      setIsBulkExporting(false)
      deselectAllItems()
    }
    const onSuccess = task => {
      if (task.result.message.includes('Failed')) {
        toast({
          title: 'There were a few failures',
          description: task.result.message,
          status: 'error',
          duration: null,
          isClosable: true
        })
      } else {
        toast({
          title: 'Export successful!',
          description: task.result.message,
          status: 'success',
          isClosable: true
        })
      }
      onFinish()
    }
    try {
      const { task_id } = await reportsApi.exportSelectedReports({ reports: selectedItems })
      await waitAndDownload(task_id, { onTaskSuccess: onSuccess, onTaskFail: onFinish, onRetryLimitReached: onFinish })
    } catch {
      toast({
        description: `Error exporting reports, please try again later`,
        status: 'error',
        isClosable: true
      })
      setIsBulkExporting(false)
    }
  }

  return {
    selectedReports: selectedItems,
    deselectAllReports: deselectAllItems,
    deselectReport: toggleItem,
    onSelectReport,
    isReportSelectable,
    onBulkExport,
    isBulkExporting,
    isReportSelected: isItemSelected
  }
}
